import { CONFIRM_PHRASE, matchesPhrase } from '@features/catalog';
import { type ClientTarget } from '@features/clients';
import { ApiError } from '@lib/api';
import { Button, Modal, TextAreaField, TextField, WriteError } from '@shared/ui';
import { useState } from 'react';

import { readAllRows, useLaunch } from '../hooks/use-bulk-migrations';
import {
    launchConfirmView,
    launchInput,
    launchSubmitBlocker,
    REARM_OPTION_HELP,
    REARM_OPTION_LABEL,
    rearmOffered,
    RESYNC_OPTION_HELP,
    RESYNC_OPTION_LABEL,
    resyncOffered,
} from '../model/launch-confirm';
import { resolveSelection, type Selection } from '../model/selection';

import type { BulkModule, JobHeader } from '../schemas/bulk-migration';

/**
 * Migrate the selected clients of a finished analysis (`platform-admin`).
 *
 * The concrete ids are read at submit, not before: "every migratable client"
 * covers pages the table never loaded, so the planned ids are read in full from
 * the runner and the operator's unticks are taken off them. The runner then
 * checks every id against the same analysis and refuses the launch, by name,
 * if one was not planned (`TENANTS_NOT_PLANNED`).
 *
 * One more checkbox — OFF until ticked — follows the module's `rerun`
 * (`launch-confirm.ts`): "Update from Salesforce" on an `update` module,
 * "Re-evaluate all records" on a `re-evaluate` one, neither on a seed-once
 * (`none`) module. The runner refuses either option on any other module
 * (`RESYNC_NOT_SUPPORTED` / `RESYNC_NOT_APPLICABLE`, or its full re-evaluation
 * refusal), so the box is never the guard.
 */
export function LaunchDialog({
    target,
    environment,
    module,
    dryRun,
    selection,
    count,
    onClose,
    onLaunched,
}: {
    target: ClientTarget;
    environment: string;
    module: BulkModule;
    dryRun: JobHeader;
    selection: Selection;
    count: number;
    onClose: () => void;
    onLaunched: (job: JobHeader) => void;
}) {
    const launch = useLaunch(target, module.moduleId, dryRun.reportId);
    const [reason, setReason] = useState('');
    const [phrase, setPhrase] = useState('');
    const [resync, setResync] = useState(false);
    const [rearmAll, setRearmAll] = useState(false);
    const offersResync = resyncOffered(module);
    const offersRearm = rearmOffered(module);
    const [resolving, setResolving] = useState(false);
    const [readError, setReadError] = useState<Error | null>(null);
    const view = launchConfirmView({
        count,
        moduleTitle: module.title,
        environment,
        resync: offersResync && resync,
        rearmAll: offersRearm && rearmAll,
    });
    const blocker = launchSubmitBlocker({ count, reason, phraseMatches: matchesPhrase(phrase) });
    const busy = resolving || launch.isPending;
    const notPlanned =
        launch.error instanceof ApiError && launch.error.code === 'TENANTS_NOT_PLANNED'
            ? launch.error.details.tenantIds
            : undefined;

    const submit = async () => {
        setReadError(null);
        setResolving(true);
        try {
            const planned = await readAllRows(target, module.moduleId, dryRun.reportId, 'planned');
            const tenantIds = resolveSelection(
                selection,
                planned.map((row) => row.tenant.tenantId),
            );
            launch.mutate(launchInput({ reason, tenantIds, module, resync, rearmAll }), {
                onSuccess: onLaunched,
            });
        } catch (error) {
            setReadError(error instanceof Error ? error : new Error(String(error)));
        } finally {
            setResolving(false);
        }
    };

    return (
        <Modal
            open
            onClose={onClose}
            dismissable={!busy}
            title={view.title}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => void submit()}
                        disabled={busy || blocker !== null}
                    >
                        {busy ? 'Starting…' : view.confirmLabel}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-3 text-[13px]">
                <p
                    role="alert"
                    className="rounded-lg border border-danger-line bg-danger-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-danger-text"
                >
                    {view.sentence}
                </p>
                {offersResync && (
                    <OptionCheckbox
                        id="bulk-launch-resync-help"
                        checked={resync}
                        onChange={setResync}
                        disabled={busy}
                        label={RESYNC_OPTION_LABEL}
                        help={RESYNC_OPTION_HELP}
                    />
                )}
                {offersRearm && (
                    <OptionCheckbox
                        id="bulk-launch-rearm-help"
                        checked={rearmAll}
                        onChange={setRearmAll}
                        disabled={busy}
                        label={REARM_OPTION_LABEL}
                        help={REARM_OPTION_HELP}
                    />
                )}
                <TextAreaField
                    label="Reason"
                    value={reason}
                    onChange={setReason}
                    placeholder="Recorded on every run this starts"
                    disabled={busy}
                />
                <TextField
                    label={`Type "${CONFIRM_PHRASE}" to confirm`}
                    value={phrase}
                    onChange={setPhrase}
                    disabled={busy}
                />
                {blocker && <p className="text-[12px] text-content-muted">{blocker}</p>}
                {readError && <WriteError error={readError} />}
                {launch.error && <WriteError error={launch.error} />}
                {Array.isArray(notPlanned) && notPlanned.length > 0 && (
                    <p className="text-[12px] text-danger-muted">
                        Not planned by this analysis: {notPlanned.map(String).join(', ')}. Untick
                        them, or analyze again.
                    </p>
                )}
            </div>
        </Modal>
    );
}

/** One optional launch checkbox, with the sentence that says what it does. */
function OptionCheckbox({
    id,
    checked,
    onChange,
    disabled,
    label,
    help,
}: {
    id: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled: boolean;
    label: string;
    help: string;
}) {
    return (
        <label className="flex items-start gap-3 text-[13px] text-content-body">
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) => {
                    onChange(event.target.checked);
                }}
                disabled={disabled}
                aria-describedby={id}
                className="mt-0.5 size-4 shrink-0 accent-brand"
            />
            <span className="grid gap-1">
                <span className="font-medium text-content">{label}</span>
                <span id={id} className="text-[12px] leading-relaxed text-content-muted">
                    {help}
                </span>
            </span>
        </label>
    );
}
